import { Badge } from '@aecom-ai-engineering/react-components';
import { ProjectRole, type User } from '@consigli/types';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDisplayName } from '@/util/format-name';

import { PeopleRow, PeopleTable, PeopleTableSkeletonRows } from './people-table';
import { projectRoleOf } from './use-project-people';

/**
 * The frame's solid pill for a project admin and its outline pill for everyone
 * else.
 *
 * `primary` renders #111827 against the frame's #1C1917 — the same near-black
 * off by a hair, and the drift the invite button already documents. Left to the
 * design system rather than overridden, so the badge stays one decision.
 *
 * `undefined` reads as the plain member. A user with no access row for this
 * project cannot come back from a list filtered by `project_ids`, so it is
 * unreachable in practice, and badging the unreachable case "Admin" is the only
 * outcome worth ruling out.
 */
const UserTypeBadge: FC<{ role: ProjectRole | undefined }> = ({ role }) => {
  const { t } = useTranslation();

  return role === ProjectRole.ADMIN ? (
    <Badge isRound label={t('project-overview.user-type-admin')} variant="primary" />
  ) : (
    <Badge isRound label={t('project-overview.user-type-user')} variant="outline" />
  );
};

/**
 * The frame's third column with nothing behind it.
 *
 * Neither `userSchema` nor `projectAccessSchema` carries a join date and
 * `GET /accounts/` returns none, so there is no value to render. Both schemas
 * are `.strict()`, which means the field cannot appear without a change in
 * @consigli/types alongside the API one — it is not a matter of reading a
 * property that is already arriving.
 *
 * The column stays, so the table keeps the frame's three-column geometry and
 * this becomes a date the day the field lands. See FA-95, which proposes
 * `project_access[].granted_at`, since the column means "joined this project"
 * rather than "joined the platform".
 *
 * The dash is hidden from assistive tech and the reason given as text: an
 * em-dash on its own is announced as "em dash" or skipped entirely, and neither
 * tells anyone the date is missing rather than empty.
 */
const UnknownDate: FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <span aria-hidden>—</span>
      <span className="sr-only">{t('project-overview.date-unknown')}</span>
    </>
  );
};

interface ProjectUsersTableProps {
  users: User[];
  /** Which project's role to badge — see `projectRoleOf`. */
  projectId: number;
  /** Draws placeholder rows under the real header. */
  isLoading?: boolean;
}

/**
 * The project's members: who they are, their role here, and when they joined.
 *
 * Owns its loading rows rather than leaving the tab to render a second
 * `PeopleTable` beside this one, which would put the column headers in two
 * places and let them drift.
 */
export const ProjectUsersTable: FC<ProjectUsersTableProps> = ({
  users,
  projectId,
  isLoading = false,
}) => {
  const { t } = useTranslation();

  return (
    <PeopleTable
      caption={t('project-overview.users-table-caption')}
      headers={[
        t('project-overview.column-name'),
        t('project-overview.column-user-type'),
        t('project-overview.column-joined-on'),
      ]}
    >
      {isLoading ? (
        <PeopleTableSkeletonRows />
      ) : (
        users.map((user) => (
          <PeopleRow
            badge={<UserTypeBadge role={projectRoleOf(user, projectId)} />}
            key={user.id}
            // Always lowercase on screen, whatever case the backend returns.
            subtitle={user.email.toLowerCase()}
            title={formatDisplayName(user.firstName, user.lastName)}
            trailing={<UnknownDate />}
          />
        ))
      )}
    </PeopleTable>
  );
};
